import { useEffect, useMemo, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { FlaskConical, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const when = (iso) => {
  if (!iso) return "";
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? "" : date.toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" });
};

// Liga/desliga, por empresa, a exclusão forçada e a limpeza de dados de teste.
// Somente o superadministrador vê e altera (o backend também exige isso).
export function ForceDeleteModeCard() {
  const [rows, setRows] = useState(null);
  const [hidden, setHidden] = useState(false);
  const [filter, setFilter] = useState("");
  const [saving, setSaving] = useState(null); // tenant_id em gravação
  const [confirm, setConfirm] = useState(null); // linha aguardando confirmação para ligar

  useEffect(() => {
    api.get("/platform/force-delete")
      .then((r) => setRows(r.data || []))
      .catch((e) => {
        if (e?.response?.status === 403) setHidden(true);
        else { setRows([]); toast.error(apiErr(e)); }
      });
  }, []);

  const save = async (row, enabled) => {
    setSaving(row.tenant_id);
    try {
      const { data } = await api.put(`/platform/force-delete/${row.tenant_id}`, { enabled });
      setRows((current) => current.map((item) => (item.tenant_id === data.tenant_id ? data : item)));
      toast.success(enabled ? `Modo de teste ligado para ${data.empresa}.` : `Modo de teste desligado para ${data.empresa}.`);
      setConfirm(null);
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setSaving(null);
    }
  };

  const onToggle = (row, enabled) => (enabled ? setConfirm(row) : save(row, false));

  const visible = useMemo(() => {
    const term = filter.trim().toLowerCase();
    return (rows || []).filter((row) => !term || row.empresa.toLowerCase().includes(term));
  }, [rows, filter]);
  const enabledCount = (rows || []).filter((row) => row.enabled).length;

  if (hidden) return null;

  return (
    <Card className={`p-5 border ${enabledCount ? "bg-amber-950/15 border-amber-800/60" : "card-metal"}`} data-testid="force-delete-mode-card">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 font-semibold text-slate-100">
            <FlaskConical className="h-5 w-5 text-amber-300" />
            Modo de teste por empresa
          </div>
          <p className="mt-1 max-w-2xl text-xs text-slate-400">
            Libera a exclusão forçada (registros com histórico) e as limpezas de dados de teste só na empresa escolhida.
            Use em empresas de teste ou de demonstração e desligue ao terminar.
          </p>
        </div>
        <span
          className={`rounded-full px-2.5 py-1 text-xs font-mono ${enabledCount ? "bg-amber-950/70 text-amber-300" : "bg-slate-800 text-slate-400"}`}
          data-testid="force-delete-mode-count"
        >
          {enabledCount} ligada{enabledCount === 1 ? "" : "s"}
        </span>
      </div>

      {rows === null ? (
        <div className="mt-4 text-sm text-slate-500">Carregando empresas...</div>
      ) : rows.length === 0 ? (
        <div className="mt-4 text-sm text-slate-500">Nenhuma empresa cadastrada.</div>
      ) : (
        <>
          {rows.length > 6 && (
            <Input
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Buscar empresa..."
              className="mt-4 max-w-xs bg-slate-950/60 border-slate-800"
              data-testid="force-delete-mode-filter"
            />
          )}
          <div className="mt-3 max-h-80 space-y-2 overflow-y-auto pr-1">
            {visible.map((row) => {
              const locked = !row.ativo && !row.enabled;
              return (
                <div
                  key={row.tenant_id}
                  className={`flex items-center justify-between gap-3 rounded-lg border px-3 py-3 ${
                    row.enabled ? "border-amber-800/60 bg-amber-950/25" : "border-slate-800 bg-slate-950/40"
                  }`}
                  data-testid={`force-delete-mode-row-${row.tenant_id}`}
                >
                  <div className="min-w-0">
                    <div className="truncate font-medium text-slate-100">
                      {row.empresa}
                      {!row.ativo && <span className="ml-2 text-xs font-normal text-slate-500">(inativa)</span>}
                    </div>
                    <div className={`mt-0.5 text-xs ${row.enabled ? "text-amber-300" : "text-slate-500"}`}>
                      {row.enabled ? "Ligado" : "Desligado"}
                      {row.updated_at ? ` · ${when(row.updated_at)}` : ""}
                      {row.updated_by ? ` por ${row.updated_by}` : ""}
                    </div>
                  </div>
                  <Switch
                    checked={row.enabled}
                    disabled={locked || saving === row.tenant_id}
                    onCheckedChange={(value) => onToggle(row, value)}
                    aria-label={`Modo de teste para ${row.empresa}`}
                    data-testid={`force-delete-mode-switch-${row.tenant_id}`}
                  />
                </div>
              );
            })}
            {visible.length === 0 && <div className="text-sm text-slate-500">Nenhuma empresa encontrada.</div>}
          </div>
        </>
      )}

      <Dialog open={!!confirm} onOpenChange={(open) => !open && setConfirm(null)}>
        <DialogContent className="bg-slate-900 border-slate-700 text-slate-100" data-testid="force-delete-mode-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl flex items-center gap-2">
              <AlertTriangle className="h-6 w-6 text-amber-400" /> Ligar modo de teste?
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-2 text-sm text-slate-300">
            <p>
              Os administradores de <b className="text-slate-100">{confirm?.empresa}</b> poderão excluir peças, máquinas,
              fornecedores e usuários com histórico, e usar as limpezas de dados de teste.
            </p>
            <p className="text-amber-200">Essas exclusões não podem ser desfeitas. Não ligue em empresa com dados reais.</p>
          </div>
          <DialogFooter className="flex-col-reverse sm:flex-row gap-2">
            <Button variant="ghost" onClick={() => setConfirm(null)} className="w-full sm:w-auto" data-testid="force-delete-mode-cancel">
              Cancelar
            </Button>
            <Button
              onClick={() => save(confirm, true)}
              disabled={saving === confirm?.tenant_id}
              className="w-full sm:w-auto bg-amber-600 hover:bg-amber-500 text-slate-950"
              data-testid="force-delete-mode-confirm"
            >
              {saving === confirm?.tenant_id ? "Ligando..." : "Ligar modo de teste"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
